import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { filterForumsAdvanced, parseSearchParams } from '../utils/advancedSearch';
import { calculateForumScore } from '../utils/forumScore';
import type { ForumBase } from '../types/forum';
import ForumRiskBadge from '../components/ForumRiskBadge';

interface Props {
  darkMode: boolean;
}

const AdvancedSearchPage: React.FC<Props> = ({ darkMode }) => {
  const [params, setParams] = useSearchParams();
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [q, setQ] = useState(params.get('q') || '');

  usePageSeo({
    title: 'Gelişmiş Forum Arama',
    description: 'Kategori, yazılım, etiket ve üye sayısına göre forum arayın.',
    keywords: 'forum arama, filtre',
    canonical: '/search',
    noindex: true,
  });

  useEffect(() => {
    supabase.from('forums').select('*').then(({ data }) => {
      setForums(data || []);
      const cats = [...new Set((data || []).map((f) => f.category))].sort();
      setCategories(cats);
    });
  }, []);

  const filters = parseSearchParams(params);
  const results = filterForumsAdvanced(forums, filters);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set('q', q);
    else next.delete('q');
    setParams(next);
  };

  const input = darkMode
    ? 'bg-gray-800 border-gray-600 text-white'
    : 'bg-white border-gray-200 text-gray-900';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <h1 className={`text-3xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Gelişmiş arama
        </h1>
        <form onSubmit={apply} className={`rounded-xl border p-4 mb-8 grid gap-4 md:grid-cols-2 ${input}`}>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="İsim, açıklama…"
            className={`rounded-lg border px-3 py-2 col-span-2 ${input}`}
          />
          <select
            className={`rounded-lg border px-3 py-2 ${input}`}
            value={filters.category || ''}
            onChange={(e) => {
              const n = new URLSearchParams(params);
              if (e.target.value) n.set('category', e.target.value);
              else n.delete('category');
              setParams(n);
            }}
          >
            <option value="">Tüm kategoriler</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Min. üye"
            className={`rounded-lg border px-3 py-2 ${input}`}
            value={filters.minMembers ?? ''}
            onChange={(e) => {
              const n = new URLSearchParams(params);
              if (e.target.value) n.set('minMembers', e.target.value);
              else n.delete('minMembers');
              setParams(n);
            }}
          />
          <input
            type="text"
            placeholder="Etiket"
            className={`rounded-lg border px-3 py-2 ${input}`}
            value={filters.tag || ''}
            onChange={(e) => {
              const n = new URLSearchParams(params);
              if (e.target.value) n.set('tag', e.target.value);
              else n.delete('tag');
              setParams(n);
            }}
          />
          <label className="flex items-center gap-2 col-span-2 text-sm">
            <input
              type="checkbox"
              checked={filters.verifiedOnly || false}
              onChange={(e) => {
                const n = new URLSearchParams(params);
                if (e.target.checked) n.set('verified', '1');
                else n.delete('verified');
                setParams(n);
              }}
            />
            Yalnızca doğrulanmış
          </label>
          <button type="submit" className="col-span-2 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Ara ({results.length})
          </button>
        </form>
        <ul className="space-y-3">
          {results.slice(0, 100).map((f) => (
            <li
              key={f.id}
              className={`rounded-lg border p-4 flex justify-between items-start gap-4 ${
                darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <div>
                <Link to={`/forum/${f.forum_id}`} className="font-semibold hover:text-indigo-500">
                  {f.name}
                </Link>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {f.category} · {f.members_count.toLocaleString('tr-TR')} üye
                </p>
                <ForumRiskBadge forum={f} className="mt-1" />
              </div>
              <span className="font-bold text-indigo-600">
                {calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year).toLocaleString(
                  'tr-TR'
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdvancedSearchPage;
